import { useState } from 'react';

import {
  Button,
  Center,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { PaginationState, SortingState } from '@tanstack/react-table';

import {
  AddCircleOutline,
  Partner,
  SearchNormalOutline,
  shadowStyles,
} from '@aslanpay/portal-common-lib';

import CreatePartnerModal from 'app/components/Partners/CreatePartner/CreatePartnerModal';
import DeletePartnerModal from 'app/components/Partners/DeletePartner/DeletePartnerModal';
import EditPartnerModal from 'app/components/Partners/EditPartner/EditPartnerModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import { Table } from 'app/components/UI/Table/Table';
import { PartnerActions } from 'app/components/UI/Table/Table.types';

import { defaultPageSize } from 'app/constants/table';

import useDebounce from 'app/hooks/useDebounce';
import useFetchPartnersPage from 'app/hooks/useFetchPartnersPage';

import getPartnerColumns from 'app/utils/table/partners';

function Partners() {
  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();

  const [partnerToEdit, setPartnerToEdit] = useState<Partner | null>(null);
  const [partnerToDeleteId, setPartnerToDeleteId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const debouncedTyping = useDebounce((value: string) => {
    setDebouncedSearchQuery(value);
    resetPagination();
  }, 500);

  const { data: partners, isLoading } = useFetchPartnersPage({
    query: debouncedSearchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  const onSearchInput = (value: string) => {
    setSearchQuery(value);
    debouncedTyping(value);
  };

  const editPartner = (partner: Partner) => {
    setPartnerToEdit(partner);
    onOpenEdit();
  };

  const deletePartner = (partner: Partner) => {
    setPartnerToDeleteId(partner.id);
    onOpenDelete();
  };

  const actions: PartnerActions = {
    edit: editPartner,
    delete: deletePartner,
  };

  const PAGE_SIZE_OPTIONS = [
    { value: 15, text: '15 rows / page' },
    { value: 25, text: '25 rows / page' },
    { value: 50, text: '50 rows / page' },
  ];

  return (
    <AuthenticatedPageWrapper title="Partners">
      <Flex
        flexDir="column"
        flexGrow={1}
        px="30px"
        py="40px"
        boxShadow={shadowStyles.dropdownMenu}
        borderRadius="10px"
        gap="20px"
      >
        <HStack>
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            Partners
          </Text>
          <Spacer />
          <Button leftIcon={<AddCircleOutline boxSize="20px" />} size="sm" onClick={onOpenCreate}>
            Create new
          </Button>
          <CreatePartnerModal isOpen={isCreateOpen} onClose={onCloseCreate} />
          <EditPartnerModal partner={partnerToEdit} isOpen={isEditOpen} onClose={onCloseEdit} />
          <DeletePartnerModal
            id={partnerToDeleteId}
            isOpen={isDeleteOpen}
            onClose={onCloseDelete}
          />
        </HStack>

        <InputGroup maxW="210px" color="brand.darkGray">
          <Input
            onChange={(event) => onSearchInput(event.target.value)}
            value={searchQuery}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>

        {isLoading ? (
          <Center width="full" height="full" flexGrow={1}>
            <Spinner color="brand.purple" size="xl" mb="50px" />
          </Center>
        ) : (
          <Table
            columns={getPartnerColumns(actions)}
            data={partners?.list || []}
            total={partners?.total || 0}
            pageNumber={partners?.pageNumber || pageIndex}
            pageSize={partners?.pageSize || pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPaginationChange={setPagination}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        )}
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default Partners;
